import { navigatorLock } from '@supabase/supabase-js';
import { noteStartup } from './startupTrace';

/**
 * The sign-in library's lock, without waiting for ever.
 *
 * Before reading or renewing the saved sign-in, the library (2.89) takes a browser-wide lock and,
 * for most operations, waits for it with no time limit. On 16 October 2026 the owner's Android
 * phone froze on "Can't reach the server" every time Fitness came back from the joined Nutrition in
 * the same tab, while two separate windows worked. The likely holder: the Fitness page left behind,
 * kept paused in the phone's memory (back-forward cache) while holding the lock, which a new Fitness
 * page then waits for indefinitely. (docs/reviews/nutrition-to-fitness-freeze.md; Codex found the
 * newer library, used by Nutrition, takes no such lock by default.)
 *
 * So: an operation that would wait for ever waits at most LOCK_WAIT_MS, then takes the lock over.
 * An operation that asks not to wait (0) or for a set time behaves exactly as before. Without the
 * browser lock API, the library's own choice applies (no lock).
 */
export const LOCK_WAIT_MS = 10000;

const timedOut = (e) => e?.isAcquireTimeout === true || e?.name === 'AbortError';

// The operation, run at most once. If another page takes the lock over while it runs, the browser
// rejects this page's lock with an AbortError ("Lock broken by another request with the 'steal'
// option") although the operation is still running. That is not a timeout: the operation is left to
// finish and its own result is returned, instead of taking the lock back and running it a second time
// (seen in Sentry on 16 October 2026, from two Cipher tabs on a computer).
const once = (fn) => {
  let running = null;
  return { run: () => (running = running || Promise.resolve().then(fn)), started: () => running !== null, result: () => running };
};

export const lockThatNeverWaitsForEver = (getLocks = () => (typeof navigator !== "undefined" ? navigator.locks : undefined), lock = navigatorLock, waitMs = LOCK_WAIT_MS) =>
  async (name, acquireTimeout, fn) => {
    const locks = getLocks();
    if (!locks) return fn();
    const op = once(fn);
    const finishIfStolen = (e) => {
      if (op.started()) return op.result();
      throw e;
    };
    if (acquireTimeout >= 0) return lock(name, acquireTimeout, op.run).catch(finishIfStolen);
    try {
      return await lock(name, waitMs, op.run);
    } catch (e) {
      if (op.started()) return op.result();
      if (!timedOut(e)) throw e;
      noteStartup('took over the sign-in lock');
      return locks.request(name, { mode: 'exclusive', steal: true }, op.run).catch(finishIfStolen);
    }
  };
